/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyledTab } from './CobrandTabs.styles';
import { Button, Heading, headingType, size, RichText } from '@marriott/mi-ui-library';
import { TabProps } from './CobrandTabs.types';
import { clsx } from 'clsx';
import { COBRAND_CARD_TYPE } from '../../modules/constants/constants';

export const CobrandTabs = ({
  componentId,
  trackingProperties,
  tabDetails,
  headerTag,
  header,
  headerFontSize,
  subHeader,
  subHeaderTag,
  subFontSize,
  descriptionText,
  descFontSize,
  contentBlockAlignment,
  layoutContainer,
  children,
  isEditMode = false,
  styleclass,
  customClass,
  componentPath,
  updateHeight,
}: TabProps) => {
  const childrenArray = React.Children.toArray(children).filter(Boolean);
  const [activeTab, setActiveTab] = useState(0);
  const descriptionTextRef = useRef<HTMLDivElement>(null);
  const tabNameTextRef = useRef<HTMLDivElement>(null);
  const borderEnabled = layoutContainer === 'with_border';
  const containerRef = useRef<HTMLDivElement>(null);

  const activeStates = useMemo(() => {
    if (isEditMode) {
      return new Array(childrenArray.length).fill(true);
    }
    return new Array(childrenArray.length).fill(false).map((_, index) => index === activeTab);
  }, [isEditMode, activeTab, childrenArray.length]);

  useEffect(() => {
    if (Array.isArray(tabDetails)) {
      for (const tab of tabDetails) {
        if (tab.tabName.toLowerCase() === COBRAND_CARD_TYPE.BUSINESSCARDTYPE || COBRAND_CARD_TYPE.PERSONALCARDTYPE) {
          const customEvent = new CustomEvent('updateCobrandDSTUrl', {
            detail: {},
          });
          document.dispatchEvent(customEvent);
        }
      }
    }
  }, [activeTab, tabDetails]);

  const handleTabClick = (tabIndex: number) => {
    setActiveTab(tabIndex);
    updateHeight(true);
  };

  const headerText = useMemo(() => {
    if (!header) return null;
    return (
      <Heading
        customClass={clsx('mb-2')}
        variation={headingType.title}
        element={headerTag}
        dangerouslySet={header}
        fontSize={headerFontSize as typeof size.small}
      />
    );
  }, [header, headerFontSize, headerTag]);

  const subHeaderText = useMemo(() => {
    if (!subHeader) return null;
    return (
      <Heading
        element={subHeaderTag}
        variation={headingType.subtitle}
        customClass={clsx(!header ? 'mt-3' : '', 'mb-3 mb-md-5')}
        dangerouslySet={subHeader}
        fontSize={subFontSize as size}
      />
    );
  }, [subHeader, subFontSize, subHeaderTag]);

  const descriptionBlock = useMemo(() => {
    if (!descriptionText) return null;
    return (
      <RichText
        text={descriptionText}
        ref={descriptionTextRef}
        componentId={componentId + '-TabContainerDescriptionText'}
        customClass={clsx(!subHeader && !header ? 'mt-3' : '', descFontSize)}
      />
    );
  }, [descriptionText, descFontSize]);

  const TabContainerHeader = useMemo(() => {
    return (
      <div
        className={clsx(
          'tab-container-header-section d-flex flex-column',
          contentBlockAlignment === 'center' ? 'align-items-center text-center' : '',
          contentBlockAlignment === 'left' ? 'align-items-start' : ''
        )}
      >
        {headerText}
        {subHeaderText}
        {descriptionBlock}
      </div>
    );
  }, [descriptionText, headerText, subHeaderText]);

  return (
    <StyledTab className={clsx(styleclass, customClass)}>
      <div className="container px-0" ref={containerRef}>
        {TabContainerHeader}
        <div id={`${componentId}`} className={clsx(`col-12 px-0`)}>
          <div
            className={clsx(
              'd-flex flex-column',
              contentBlockAlignment === 'center' ? 'align-items-center' : '',
              contentBlockAlignment === 'left' ? 'align-items-start' : ''
            )}
          >
            <ul
              role="tablist"
              className={clsx(
                `m-standard-tab-list`,
                contentBlockAlignment === 'center' ? 'justify-content-md-center' : '',
                contentBlockAlignment === 'left' ? 'justify-content-start' : ''
              )}
            >
              {tabDetails?.map((tab, key) => {
                const { tabName = '', tabSubHeader } = tab;
                return (
                  <li
                    key={key}
                    className={clsx('custom_click_track', `m-standard-tab-list-item pb-0 text-center mr-0`, {
                      active: activeTab === key,
                    })}
                    {...{
                      custom_click_track_value: `${tabName}-tab`,
                    }}
                    role="tab"
                    aria-label={tabName}
                  >
                    <Button
                      ariaExpanded={tabName}
                      custom_click_track_value={`${tabName}-tab`}
                      trackingProperties={trackingProperties}
                      callback={() => handleTabClick(key)}
                    >
                      <RichText
                        text={tabName}
                        ref={tabNameTextRef}
                        componentId={componentId + '-TabContainerTabName'}
                        customClass={clsx(
                          contentBlockAlignment === 'center' ? 'text-center' : '',
                          ' tabTitleText mb-2',
                          componentPath ? descFontSize : 't-font-m'
                        )}
                      />
                      {tabSubHeader && (
                        <RichText
                          text={tabSubHeader}
                          ref={tabNameTextRef}
                          componentId={componentId + '-TabContainertabSubHeader'}
                          customClass={clsx(
                            contentBlockAlignment === 'center' ? 'text-center' : '',
                            'tab-subtitle',
                            componentPath ? descFontSize : 't-font-m'
                          )}
                        />
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div
          className={clsx(
            'tabpage-container',
            borderEnabled && 'tabpage-border',
            contentBlockAlignment === 'center' ? 'pt-md-3' : ''
          )}
        >
          {childrenArray?.map((child, index) => (
            <div key={index} className={activeStates[index] ? 'd-block' : 'd-none'}>
              {React.isValidElement(child)
                ? React.cloneElement(child, { key: index + '-activeindex-' + activeTab, ...child.props })
                : child}
            </div>
          ))}
        </div>
      </div>
    </StyledTab>
  );
};
